"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = transferUserDataForEditableTextLayers;
exports.transferUserDataForEditableTextLayer = transferUserDataForEditableTextLayer;
const Layer_1 = require("../../models/layers/Layer");
const compareByReadingOrder_1 = __importDefault(require("./compareByReadingOrder"));
const EditableTextLayer_1 = require("../../models/layers/EditableTextLayer");
const printables_1 = require("../printables");
const lodash_clonedeep_1 = __importDefault(require("lodash.clonedeep"));
const getInvalidCharactersForFoilStamp_1 = require("../../utils/getInvalidCharactersForFoilStamp");
const html_1 = require("./html");
const requiresUpdate_1 = require("./requiresUpdate");
const transferUserDataForLayersHelper_1 = require("./transferUserDataForLayersHelper");
/**
 * This function finds the editable text layers and transfers any user data that may have existed to the new layers in reading order and returns the new layers
 * @param printableSurface Printable surface, needed for determining the size of the layer and the text asset id
 * @param oldLayers Layers from the old design that may contain user data
 * @param templateLayers Layers for the new design from the HAMMER template
 * @param ignoreTemplateLayers Optional parameter, if true, will transfer the editable text layers as is and ignore the template
 * @returns Editable text layers from the template filled with prior user data if available
 */
function transferUserDataForEditableTextLayers(printableSurface, oldLayers, templateLayers) {
  let ignoreTemplateLayers = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  if (ignoreTemplateLayers) {
    // If ignoreTemplateLayers is true (applicable for book interiors) we want to keep the text layers as they are, regardless of the new design's text layers
    // Find the max z index for the new design so that we can ensure the text layers, once transferred, stay on top
    const maxZIndex = Math.max(0, ...templateLayers.filter(layer => !(0, EditableTextLayer_1.isEditableTextLayer)(layer)).map(l => l.zIndex || 0));
    // Find all the editable text layers from the last design
    const oldEditableTextLayers = oldLayers.filter(layer => (0, EditableTextLayer_1.isEditableTextLayer)(layer)).sort(compareByReadingOrder_1.default);
    // return the editable text layers from the old design, as is, only adjusting the z index to ensure the text is on top
    return oldEditableTextLayers.map((layer, i) => Object.assign(Object.assign({}, layer), {
      zIndex: maxZIndex + i + 1
    }));
  } else {
    // Else, such as with covers or wooden boxes, we want to try to transfer editable text layer data from the old design to the new design
    let oldEditableTextLayers = oldLayers.filter(layer => (0, EditableTextLayer_1.isEditableTextLayer)(layer)).sort(compareByReadingOrder_1.default);
    const newEditableTextLayers = templateLayers.filter(layer => (0, EditableTextLayer_1.isEditableTextLayer)(layer)).map(layer => (0, lodash_clonedeep_1.default)(layer)) // Ensure we're not modifying the original layer object from the template
    .sort(compareByReadingOrder_1.default); // Ensure that data is transferred in reading order
    newEditableTextLayers.forEach(newLayer => {
      let oldLayer;
      // if the new layer we're transferring data too is on the right, find the first available old layer also on the right
      if (newLayer.id.includes('--SHIFTED')) {
        oldLayer = oldEditableTextLayers.find(l => l.id.includes('--SHIFTED'));
      } else {
        oldLayer = oldEditableTextLayers.find(l => !l.id.includes('--SHIFTED'));
      }
      // if there is default content in the new layer, create the user data according to the given default data
      if (newLayer.defaultData.content) {
        (0, printables_1.createDefaultLayers)(newLayer, printableSurface);
      }
      if (oldLayer) {
        // If an old layer was found, copy the data from the old layer, to the new layer
        if ((0, EditableTextLayer_1.isFilledEditableTextLayer)(oldLayer) && oldLayer.defaultData.content !== oldLayer.userData.rawText) {
          // If the old layer has user data AND that user data is not the just the default content, transfer the data to the new layer
          transferUserDataForEditableTextLayer(oldLayer, newLayer, printableSurface);
        }
        // then remove it from the list, so we don't transfer the same data multiple times
        const oldLayerId = oldLayer.id;
        oldEditableTextLayers = oldEditableTextLayers.filter(l => l.id !== oldLayerId);
      }
    });
    return newEditableTextLayers;
  }
}
/**
 * TODO: Text restriction: invalid chars
 * @param oldLayer Old editable text layer with user data
 * @param newLayer New editable text layer from template
 */
function transferUserDataForEditableTextLayer(oldLayer, newLayer, printableSurface) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
  newLayer.userData = (0, lodash_clonedeep_1.default)(oldLayer.userData);
  // If there is a min font size on the new layer and the min font size on the old layer
  // was either undefined or less than the min font size on the new layer, we need to make sure to update
  // any user content that is too small for the new layer
  const oldMinimumFontSize = (_a = oldLayer.defaultData.styleRestrictions) === null || _a === void 0 ? void 0 : _a.minimumFontSize;
  const newMinimumFontSize = (_b = newLayer.defaultData.styleRestrictions) === null || _b === void 0 ? void 0 : _b.minimumFontSize;
  if (newMinimumFontSize && (!oldMinimumFontSize || oldMinimumFontSize < newMinimumFontSize)) {
    const fontSizeParts = (oldLayer.userData.content || '').split('data-font-size-points="');
    for (let i = 1; i < fontSizeParts.length; i++) {
      const fontSize = Number(fontSizeParts[i].split('"')[0]);
      if (isNaN(fontSize) || fontSize < newMinimumFontSize) {
        const adjustedFontSizePart = [newMinimumFontSize, ...fontSizeParts[i].split('"').slice(1)].join('"');
        fontSizeParts[i] = adjustedFontSizePart;
      }
    }
    newLayer.userData.content = fontSizeParts.join('data-font-size-points="');
  }
  const PARAGRAPH_REGEX = /(<p(>|.*?[^?]>))(.*)(<\/p>)/gm;
  const INNER_MOST_SPAN_REGEX = /(<span\b[^>]*>(?:(?=([^<]+))\1|(?!<span\b[^>]*>)))(.*?)(<\/span>)/gm;
  // if the oldLayer was foil and the new layer isn't, add back in the default colour
  const stripInlineColours = (oldLayer.printType === 'digital_foil' && !newLayer.printType || !oldLayer.printType && newLayer.printType === 'digital_foil') && ((_c = newLayer.defaultData.style) === null || _c === void 0 ? void 0 : _c.color);
  const styleRestricted = ((_d = newLayer.defaultData.styleRestrictions) === null || _d === void 0 ? void 0 : _d.fontStyle) === 'fixed' && ((_e = newLayer.defaultData.style) === null || _e === void 0 ? void 0 : _e.fontStyle) && newLayer.defaultData.style.fontStyle === 'bold';
  const alignRestricted = ((_f = newLayer.defaultData.styleRestrictions) === null || _f === void 0 ? void 0 : _f.textAlign) === 'fixed' && ((_g = newLayer.defaultData.style) === null || _g === void 0 ? void 0 : _g.textAlign);
  const shouldCheckFontFamily = (0, requiresUpdate_1.requiresUpdateToFontFamily)(oldLayer, newLayer);
  const caseRestricted = ((_h = newLayer.defaultData.style) === null || _h === void 0 ? void 0 : _h.textTransform) === 'uppercase';
  const charLimited = ((_j = newLayer.textRestrictions) === null || _j === void 0 ? void 0 : _j.charLength) && parseInt((_k = newLayer.textRestrictions) === null || _k === void 0 ? void 0 : _k.charLength) > 0;
  const shouldCheckValidCharacters = newLayer.printType === 'foil_stamp';
  const newLayerFontSizeInPt = (_m = (_l = newLayer.defaultData) === null || _l === void 0 ? void 0 : _l.style) === null || _m === void 0 ? void 0 : _m.fontSize;
  const paragraphRegex = PARAGRAPH_REGEX.exec(newLayer.userData.content || '');
  // we only wanna do this styleRestrictions block if there are stylerestrictions to restric
  if ((styleRestricted || alignRestricted || shouldCheckFontFamily || caseRestricted || charLimited || stripInlineColours || shouldCheckValidCharacters) && paragraphRegex != null) {
    // const paragraphTag = paragraphRegex[0]; //akin to p.outerHTML - keeping this in since it's useful for debugging
    let paragraphOuterTag = paragraphRegex[1];
    let paragraphContent = paragraphRegex[3];
    let paragraphCloseTag = paragraphRegex[4];
    // first, do check on things that could change the wrapping parapgraph tag
    if (styleRestricted || alignRestricted) {
      const paragraphStyles = (0, transferUserDataForLayersHelper_1.getInlineStylesForOpeningTag)(paragraphOuterTag);
      // check to see if we need to update the alignment vs add the new fixd alignment.
      if (paragraphStyles.size === 0) {
        // we need to add the style atribute to the paragraph tag before adding the style
        paragraphOuterTag = '<p style=""' + paragraphOuterTag.substring(2);
      }
      if (alignRestricted) {
        paragraphStyles.set('text-align', (_o = newLayer.defaultData.style) === null || _o === void 0 ? void 0 : _o.textAlign);
      }
      if (styleRestricted) {
        paragraphStyles.set('font-weight', (_p = newLayer.defaultData.style) === null || _p === void 0 ? void 0 : _p.fontStyle);
      }
      paragraphOuterTag = paragraphOuterTag.replace(/(style=")([^"]+)*?(")/gm, `$1${(0, transferUserDataForLayersHelper_1.stylesObjectToString)(paragraphStyles)}$3`);
    }
    if (stripInlineColours) {
      // first, strip existing colour out of any existing elements in the content
      paragraphContent = paragraphContent.replace(/(?:<(?:span)[^<]*)(style="[^"]*")(?:[^>]*>)/gm, g1 => {
        const strippedColour = g1.replace(/color:.+;/, '');
        return strippedColour;
      });
      // create a wrapper span inside the main paragraph tag with the default colour - this will "force" a asset regeneration
      if (!newLayer.printType) {
        paragraphOuterTag += `<span style="color:${(_q = newLayer.defaultData.style) === null || _q === void 0 ? void 0 : _q.color};">`;
        paragraphCloseTag = '</span>' + paragraphCloseTag;
      }
    }
    const innerSpan = INNER_MOST_SPAN_REGEX.exec(paragraphContent);
    // We have a span with text content. The regex checks for innermost because we could have N number of style wrapping spans from CKEditor
    // looks like <p style="...alignment"><span style="...color">COVER TEXT</span></span></p>
    if (shouldCheckFontFamily || caseRestricted || charLimited || shouldCheckValidCharacters) {
      if (innerSpan !== null) {
        // const innerSpanTag = innerSpan[0]; // akin to span.outerHTML
        let innerSpanContent = innerSpan[1];
        let innerSpanTextContent = innerSpan[3];
        if (shouldCheckFontFamily) {
          const spanStyles = (0, transferUserDataForLayersHelper_1.getInlineStylesForOpeningTag)(innerSpanContent);
          // instead of doing some fancy replace, we will find if there is a font worth
          const currentFontFamily = spanStyles.get('font-family');
          if (currentFontFamily) {
            const isValidFont = ((_s = (_r = newLayer.defaultData) === null || _r === void 0 ? void 0 : _r.styleRestrictions) === null || _s === void 0 ? void 0 : _s.fontFamily).find(validFont => {
              // getting the font list from ckeditor and the font from the rendered div returns various implementations
              // of double and single quotes so strip all that out to compare
              return validFont.replace(/['"]/g, '').indexOf(currentFontFamily.replace(/['"]/g, '')) >= 0;
            });
            if (!isValidFont) {
              spanStyles.set('font-family', (_t = newLayer.defaultData.style) === null || _t === void 0 ? void 0 : _t.fontFamily);
              innerSpanContent = innerSpanContent.replace(/(style=")([^"]+)*?(")/gm, `$1${(0, transferUserDataForLayersHelper_1.stylesObjectToString)(spanStyles)}$3`);
            } // else no fonts are defined in the inline styles so nothing can be invalid
          } // else no style attribute so nothing can be invalid
        }

        if (shouldCheckValidCharacters && innerSpanTextContent && newLayerFontSizeInPt) {
          innerSpanTextContent = (0, html_1.enforceInvalidCharactersOnHTML)(innerSpanTextContent, newLayerFontSizeInPt);
        }
        const newLayerCharLength = parseInt((_u = newLayer.textRestrictions) === null || _u === void 0 ? void 0 : _u.charLength);
        if (charLimited && innerSpanTextContent && !isNaN(newLayerCharLength)) {
          innerSpanTextContent = (0, html_1.enforceCharLengthOnHTML)(innerSpanTextContent, newLayerCharLength);
        }
        if (caseRestricted && innerSpanTextContent) {
          innerSpanTextContent = (0, html_1.enforceTextTransformOnHTML)(innerSpanTextContent);
        }
        paragraphContent = `${innerSpanContent}${innerSpanTextContent}</span>`;
      } else {
        // CKEditor will also produce content with no inner span (and it lets the outer styles style the content).
        // in this case we're just looking for <p style="...alignment">COVER TEXT</p>
        if (charLimited && paragraphContent && paragraphContent.length > parseInt((_v = newLayer.textRestrictions) === null || _v === void 0 ? void 0 : _v.charLength)) {
          paragraphContent = paragraphContent.substring(0, parseInt((_w = newLayer.textRestrictions) === null || _w === void 0 ? void 0 : _w.charLength));
        }
        if (caseRestricted) {
          paragraphContent = paragraphContent.toUpperCase();
        }
        if (shouldCheckValidCharacters && newLayerFontSizeInPt) {
          const invalidCharsRegex = (0, getInvalidCharactersForFoilStamp_1.getInvalidCharactersForFoilStamp)(newLayerFontSizeInPt);
          paragraphContent = invalidCharsRegex ? paragraphContent.replace(invalidCharsRegex, '') : paragraphContent;
        }
      }
    }
    // gather all changes and set them on the userData.content
    let potentialNewContent = paragraphOuterTag + paragraphContent + paragraphCloseTag;
    const printTypesWithNoEmojis = [Layer_1.LAYER_PRINT_TYPE_FOIL_STAMP, Layer_1.LAYER_PRINT_TYPE_DIGITAL_FOIL];
    if (!printTypesWithNoEmojis.includes((oldLayer === null || oldLayer === void 0 ? void 0 : oldLayer.printType) || '') && printTypesWithNoEmojis.includes((newLayer === null || newLayer === void 0 ? void 0 : newLayer.printType) || '')) {
      potentialNewContent = (0, transferUserDataForLayersHelper_1.removeImgTags)(potentialNewContent);
    }
    if (potentialNewContent != newLayer.userData.content) {
      newLayer.userData.content = potentialNewContent;
    }
  }
  // TODO: Once we upgrade to react 18 and are able to use the jsdom lib, we need to ditch the code above for the code below.
  // const doc = new JSDOM(newLayer.userData.content);
  // const pTag = doc.window.document.querySelector('p');
  // if (pTag && newLayer.defaultData.styleRestrictions?.fontStyle === 'fixed' && newLayer.defaultData.style?.fontStyle) {
  //   pTag.style.fontWeight = newLayer.defaultData.style?.fontStyle;
  //   newLayer.userData.content = pTag.outerHTML;
  // }
  newLayer.userData.assetId = (0, EditableTextLayer_1.generateTextAssetId)(newLayer.width, newLayer.height, newLayer.userData.content || '', printableSurface);
}